import { useMemo } from 'react';

interface Props {
  inviteCode: string; // e.g., "INV-a3f8c2d1"
  size?: number;
}

export function InviteTokenFingerprint({ inviteCode, size = 42 }: Props) {
  const fingerprint = useMemo(() => {
    if (!inviteCode || !inviteCode.trim()) {
      return {
        backgroundColor: '#1e293b',
        pattern: 'none',
      };
    }
    // Deterministic hash from invite code
    const clean = inviteCode.trim().toUpperCase();
    const hash = clean.split('').reduce((acc, char) => {
      return ((acc << 5) - acc) + char.charCodeAt(0) | 0;
    }, 0);

    // Generate color from hash
    const hue = Math.abs(hash % 360);
    const saturation = 70 + (Math.abs(hash >> 8) % 30); // 70-100%
    const lightness = 40 + (Math.abs(hash >> 16) % 20); // 40-60%

    return {
      backgroundColor: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
      pattern: generatePattern(hash),
    };
  }, [inviteCode]);

  return (
    <div
      className="invite-fingerprint"
      style={{
        width: size,
        height: size,
        backgroundColor: fingerprint.backgroundColor,
        backgroundImage: fingerprint.pattern,
        borderRadius: '6px',
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.2s ease',
      }}
      title={`Token Fingerprint: ${inviteCode || 'None'}`}
    />
  );
}

function generatePattern(hash: number): string {
  const patternType = Math.abs(hash) % 3;
  switch (patternType) {
    case 0:
      return 'repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,0.25) 8px, rgba(255,255,255,0.25) 16px)';
    case 1:
      return 'radial-gradient(circle, rgba(255,255,255,0.3) 2px, transparent 2px)';
    case 2:
    default:
      return 'linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)';
  }
}
